import { LightningElement, wire } from "lwc";
import { LabelTranslations, MultiLabelAdapter, t } from "tbme/localization";
import { QueryAdapter, QueryAdapterValue } from "tbme/queryAdapter";
import { CardState } from "../../tbui/card/types";
import {
  GetUserCertifications,
  GetUserCertificationsVariables,
  GetUserCertifications_profile_PublicProfile_credential_certifications,
  GetUserCertifications_profile_PublicProfile_credential_messages as Message,
} from "./gql/GetUserCertifications";
import { Location, StatusColor } from "../../../gql/types";
import { showToast } from "tds/toaster";
import { USER_CERTIFICATIONS_QUERY } from "./query";
import capitalize from "lodash/capitalize";

export interface Certification {
  product: string;
  title: string;
  publicDescription: string;
  dateExpired: string | null;
  dateCompleted: string;
  maintenanceDueDate: string | null;
  logoUrl: string;
  infoUrl: string;
  status: {
    color: string;
    title: string;
    date: string;
  };
  downloadUrl: string;
  cta: {
    url: string | null;
    label: string | null;
  } | null;
  hasDivider: boolean;
}

export const colorMap: Record<StatusColor, string> = {
  [StatusColor.GRAY]: "--tds-color-meteorite",
  [StatusColor.GREEN]: "--tds-color-evergreen",
  [StatusColor.RED]: "--tds-color-ruby",
};
export default class extends LightningElement {
  private isSelf: boolean = false;
  private labels: LabelTranslations = undefined!;
  private firstFetchLoading: boolean = true;
  private cardState: CardState = "default";
  private certifications: Certification[] | null = Array<Certification>();
  private messagesOnly: boolean = false;
  private topMessages: Message[] | null = Array<Message>();
  private bottomMessages: Message[] | null = Array<Message>();

  private variables: GetUserCertificationsVariables = {
    queryProfile: false,
  };

  /** Determines the title of the component */
  get cardTitle() {
    return this.certifications &&
      this.certifications.length > 1 &&
      !this.messagesOnly
      ? `${this.certifications.length} ${this.labels.title}`
      : this.labels.title;
  }

  /** Should the component be visible */
  get isVisible() {
    // TODO: isVisible
    return true;
  }

  /** Shows or hides the card actions (ie. Search and Filter) */
  get showActions() {
    return (
      (this.visibleCertifications!.length > 0 && !this.messagesOnly) ?? false
    );
  }

  /** The certifications that the user sees */
  get visibleCertifications() {
    return this.certifications;
  }

  /** The collection of filter options */
  get filterOptions() {
    const options = [
      {
        label: this.labels.filterByProduct,
        value: this.labels.filterByProduct,
      },
      {
        label: this.labels.filterByStatus,
        value: this.labels.filterByStatus,
      },
      {
        label: this.labels.filterByMostRecent,
        value: this.labels.filterByMostRecent,
      },
    ];
    return options;
  }

  /** Formats the certifications to be displayed */
  private formatCertifications(
    edges:
      | (GetUserCertifications_profile_PublicProfile_credential_certifications | null)[]
      | null
  ): Certification[] {
    if (!edges) return [];
    return edges.map((item, index) => {
      return {
        product: item!.product,
        title: item!.title,
        publicDescription: item!.publicDescription,
        dateExpired: item?.dateExpired,
        dateCompleted: item!.dateCompleted,
        maintenanceDueDate: item?.maintenanceDueDate,
        logoUrl: item!.logoUrl,
        infoUrl: item!.infoUrl,
        status: {
          color: colorMap[item!.status.color],
          title: capitalize(item!.status!.title!),
          date: item!.status.date,
        },
        downloadUrl: item!.downloadUrl,
        cta: {
          url: item!.cta?.url,
          label: item!.cta?.label,
        },
        hasDivider: index !== edges.length - 1,
      } as Certification;
    });
  }

  @wire(QueryAdapter, {
    query: USER_CERTIFICATIONS_QUERY,
    variables: "$variables",
  })
  private handleResult(result: QueryAdapterValue<GetUserCertifications>) {
    const { data, loading, error, errors } = result;
    // If/when the id is resolved from local state, toggle the `queryProfile` variable which will trigger
    // the API call.
    if (!this.variables.queryProfile && data && data.profileUser) {
      const { id, isSelf } = data.profileUser;
      const queryProfile = !!id;
      this.isSelf = isSelf;

      if (queryProfile) {
        this.variables = {
          ...this.variables,
          queryProfile,
        };
      }
    }

    if (loading) {
      this.cardState = "loading";
      return;
    }

    if (
      error ||
      errors ||
      !data.profile ||
      data.profile.__typename !== "PublicProfile"
    ) {
      console.log(error);
      this.cardState = "loadFailed";
      this.firstFetchLoading = false;
      return;
    }
    this.messagesOnly = data.profile.credential?.messagesOnly ?? false;
    this.certifications = this.formatCertifications(
      data.profile.credential?.certifications ?? []
    );
    this.bottomMessages = [];
    this.topMessages = [];
    data.profile.credential?.messages?.forEach((item) => {
      switch (item.location) {
        case Location.BOTTOM:
          this.bottomMessages?.push(item);
          break;
        case Location.TOP:
          this.topMessages?.push(item);
          break;
        case Location.TOAST:
          showToast({ message: item.body, variant: "error" });
          break;
      }
    });
    this.cardState = "default";
    this.firstFetchLoading = false;
  }

  @wire(MultiLabelAdapter, {
    labels: [
      "certifications.title",
      "certifications.searchPlaceholder",
      "certifications.filterByProduct",
      "certifications.filterByStatus",
      "certifications.filterByMostRecent",
    ],
  })
  private handleLabels(labels: { certifications: LabelTranslations }) {
    this.labels = {
      ...labels.certifications,
    };
  }
}
